import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { getIn, useFormikContext } from "formik";
import { makeStyles } from "tss-react/mui";
import { Theme } from "@mui/material";
import { GridRowModel, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import AddBoxRoundedIcon from "@mui/icons-material/AddBoxRounded";

import {
  ApiResultsType,
  Item,
  Option,
  SelectFilterItemSortedFormDetails,
} from "../interfaces";
import { GenericObject } from "../../services/Price";
import { isObject } from "../../services/utils";
import { useDebounce } from "../../hooks/debounce";
import {
  checkIfAllWatchFieldsValueAreFilled,
  getSelectFilterValues,
  useCreateParams,
  useGetWatchedFieldValue,
} from "../utils";
import { ConfigurationPageTable } from "../interfaces";
import { TooltipLabel } from "../../components/Tooltips";
import ColorSwatch from "../../components/ColorSwatch";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";

const useStyles = makeStyles()((theme: Theme) => ({
  // Only the field's width: fill and outline come from the wrapper and the theme.
  field: {
    flex: "0 1 224px",
  },
  labelIcon: {
    fill: theme.vars.palette.text.secondary,
    verticalAlign: "middle",
    height: "15px",
    cursor: "pointer",
  },
}));

const CustomAdornment = ({
  styles,
  onOpenSubForm,
}: {
  styles: any;
  onOpenSubForm: () => void;
}) => {
  return (
    <AddBoxRoundedIcon className={styles} onClick={() => onOpenSubForm()} />
  );
};

export const SelectFilterItemSorted = ({
  filter,
  field,
  meta,
  helpers,
  row,
  dynamicPath,
  rows,
  multipleSelection,
  isMappingUhCat,
  onDisplaySubCreationForm,
}: {
  filter: SelectFilterItemSortedFormDetails;
  field: any;
  meta: any;
  helpers: any;
  classes: any;
  row?: GridRowModel;
  dynamicPath?: boolean;
  rows?: GridRowModel[];
  multipleSelection?: GridRowSelectionModel;
  isMappingUhCat?: boolean;
  onDisplaySubCreationForm?: (
    formOptions: ConfigurationPageTable,
    parentFormValues: GenericObject,
    initialValues: GenericObject
  ) => void;
}) => {
  const intl = useIntl();
  const { classes: basicsClasses } = useStyles();
  const { values, setFieldValue, initialValues } = useFormikContext<any>();
  const [inputInitialValues, setInputInitialValues] = useState<string | null>(
    field.label ? field.label : null
  );

  const watchedFieldValue = useGetWatchedFieldValue(
    values,
    field.name,
    filter.watchField
  );

  const params = useCreateParams(
    filter,
    dynamicPath,
    row,
    rows,
    watchedFieldValue,
    multipleSelection,
    isMappingUhCat
  );
  const debounceSearchValue = useDebounce(inputInitialValues, 400);

  const isDisable: boolean = useMemo(() => {
    // Disabled selector (in case of dynamic values) if selector on which it depends is empty
    let disable =
      (dynamicPath &&
        filter.watchField &&
        !checkIfAllWatchFieldsValueAreFilled(params, filter.watchField)) ||
      filter.readonly;

    // In case of watchFilledField, disable input if other is filled and enable if both field are filled
    if (filter.watchFilledField && values[filter.watchFilledField]) {
      disable = true;
      if (field.value) {
        disable = false;
      }
      // In Case of enableField, disable input if other input is not filled or checked as true
    } else if (filter.enableField && values) {
      if (!values[filter.enableField as string]) {
        disable = true;
      }
      // New way to check if should be disable, should replace all other ways
    } else if (filter.relatedInputAction && values) {
      if (
        filter.relatedInputAction.action === "enable" &&
        typeof filter.relatedInputAction.relatedInput === "string" &&
        values[filter.relatedInputAction.relatedInput.split(".").join("__")]
      ) {
        disable = true;
      } else {
        disable = false;
      }
    }

    return !!disable;
  }, [
    dynamicPath,
    filter.watchField,
    filter.readonly,
    filter.enableField,
    filter.relatedInputAction,
    filter.watchFilledField,
    values,
    field.value,
    params,
  ]);

  const query = useQuery(
    [filter.api, params, debounceSearchValue],
    () =>
      getSelectFilterValues<Item>(
        filter.api as string,
        params,
        debounceSearchValue
      ),
    {
      enabled: !isDisable,
      select: (data) => {
        if (
          filter.apiResultsType &&
          filter.apiResultsType === ApiResultsType.Array
        ) {
          const generateData: Option[] = data.results.map((option) => ({
            id: option as unknown as string,
            label: option as unknown as string,
            isHardCodedOption: false,
          }));

          return generateData;
        } else {
          const generateData = data.results.map((option) => {
            const buildedOptionValues: Option = {
              id: option.id,
              label: option[filter.labelKey as string],
              isHardCodedOption: false,
            };

            if (filter.keepApiData) {
              filter.keepApiData.forEach(
                (key) => (buildedOptionValues[key] = option[key])
              );
            }

            return buildedOptionValues;
          });

          if (multipleSelection || filter.hasCheckAllOption) {
            generateData.unshift({
              id: -1,
              label: filter.hasCheckAllOption
                ? intl.formatMessage({ id: "ALL" })
                : "--",
              isHardCodedOption: true,
            });
          }

          return generateData;
        }
      },
    }
  );

  // ⚠️ Hoisted so the Autocomplete and the sub-form adornment cannot diverge. It was
  // computed inline on `disabled` alone, and `CustomAdornment` is a bare icon with its
  // own `onClick` inside `startAdornment` — MUI's `disabled` stops the input, not an
  // arbitrary adornment child — so the "create a new one" button stayed clickable while
  // the select was disabled, including while its options were still loading.
  //
  // A non-empty search term means the field was already enabled for the current
  // params, so a search refetch must not disable it: MUI blurs a disabled field
  // and `clearOnBlur` wipes the text the user is typing.
  const isInteractionDisabled =
    isDisable ||
    (!!filter.watchField && !query.isSuccess && !debounceSearchValue);

  // A value seeded with an id only (no label known at open time) takes its
  // label from the loaded options, and is written back so the rest of the
  // form sees a complete option.
  const selectedOption = useMemo<Option | null>(() => {
    if (filter.apiResultsType || !isObject(field.value)) return null;
    return query.data?.find((opt) => opt.id === field.value.id) ?? null;
  }, [filter.apiResultsType, field.value, query.data]);

  useEffect(() => {
    // `undefined` label only: an empty string is a resolved (if blank) label,
    // writing it again would re-run this on every render.
    if (selectedOption && field.value.label === undefined) {
      helpers.setValue(selectedOption);
    }
  }, [selectedOption, field.value, helpers]);

  const displayedValue: Option | null = filter.apiResultsType
    ? // Use this to fix language selecor in mail form
      (field.value?.id &&
        query.data?.find((opt) => opt.id === field.value.id)) ||
      null
    : selectedOption ?? (field.value || null);

  const colorKey = filter.renderOption?.key;
  const swatchOf = (option: Option | null) =>
    colorKey && option?.[colorKey] ? (
      <ColorSwatch color={option[colorKey]} sx={{ mr: 1 }} />
    ) : null;

  const handleOpenSubForm = () => {
    if (filter.subCreationForm && onDisplaySubCreationForm) {
      onDisplaySubCreationForm(
        filter.subCreationForm as ConfigurationPageTable,
        values,
        initialValues
      );
    }
  };

  const handleChange = (newValue: Option | null | string) => {
    // In case of filter.watchFilledField we doesn't want to clean value
    if (!filter.watchFilledField) {
      // Clean seletor (in case of dynamic values) if selector on which it depends change
      if (filter.watchedBy && Array.isArray(filter.watchedBy)) {
        filter.watchedBy.forEach((input) => {
          setFieldValue(input.split(".").join("__"), null);
        });
      } else if (filter.watchedBy) {
        // In case of nested form, we possibly want to look after field inside the form or in the parent form
        // We make the differentiation with "[]" inside the watchedBy
        if (filter.watchedBy.includes("[]")) {
          const splitParent = filter.watchedBy.split("[].");
          const transformedWatchKey = splitParent[1]
            ? splitParent[1].replace(/\./g, "__")
            : null;

          if (transformedWatchKey && splitParent[0]) {
            const valuesArr = getIn(values, splitParent[0]);
            if (Array.isArray(valuesArr)) {
              valuesArr.forEach((_: GenericObject, index: number) => {
                const watchedFieldPath = [
                  `${splitParent[0]}[${index}]`,
                  transformedWatchKey,
                ];
                setFieldValue(watchedFieldPath.join("."), "");
              });
            }
          }
        } else {
          // Get path of watchedBy input (take into account nested field)
          const fieldPathArray = field.name.split(".");
          const parentPath = fieldPathArray.slice(0, -1);
          const transformedWatchKey = filter.watchedBy.replace(/\./g, "__");
          const watchedFieldPath = [...parentPath, transformedWatchKey];

          setFieldValue(watchedFieldPath.join("."), null);
        }
      }
    }
    helpers.setValue(newValue);

    if (newValue && filter.onValueChange) {
      filter.onValueChange({ row, setFieldValue });
    }
  };

  return (
    <div className={basicsClasses.field}>
      <CustomAutocomplete
        // ⚠️ `isDisable` alone leaves a dependent select interactive while its options
        // are still loading, or after the request failed — an empty dropdown that reads
        // as "no results" rather than "not ready". So a field with `watchField` also
        // waits for its query to succeed; see `isInteractionDisabled`.
        //
        // Deliberately not folded into `isDisable`: the query is gated on `!isDisable`,
        // so making that depend on the query would mean it never runs. And deliberately
        // scoped to `watchField` — a plain select has nothing to wait for, and
        // disabling every one in the app while it loads is not this change's business.
        disabled={isInteractionDisabled}
        error={meta.touched && !!meta.error}
        filterOptions={(options) => options}
        freeSolo={filter.freeSolo}
        fullWidth
        getOptionLabel={(option) => {
          if (typeof option != "string") {
            return option.label || "";
          } else {
            return option;
          }
        }}
        helperText={meta.touched && meta.error}
        isOptionEqualToValue={(option: Option, value: string | Option) => {
          if (typeof value === "string") return option.id.toString() === value;
          return option.id === value.id;
        }}
        label={
          filter.tooltip ? (
            <TooltipLabel
              label={intl.formatMessage({ id: filter.label })}
              tooltipLabel={intl.formatMessage({ id: filter.tooltip })}
              translateTooltip={false}
            />
          ) : (
            intl.formatMessage({ id: filter.label })
          )
        }
        loading={query.isLoading}
        options={query.data || []}
        onBlur={field.onBlur}
        onChange={(e, newValue) => handleChange(newValue)}
        onClick={(e) => {
          /* This one is to avoid the table re-rendering on the focus loss */
          e.stopPropagation();
        }}
        onInputChange={(event, newInputValue, reason) => {
          if (filter.freeSolo) {
            helpers.setValue(newInputValue);
          }
          if (reason !== "reset") {
            setInputInitialValues(newInputValue);
          } else if (newInputValue === "") {
            setInputInitialValues(null);
          }
        }}
        placeholder={intl.formatMessage({ id: filter.label })}
        renderOption={(props, option) => {
          return (
            <li {...props} key={option.id}>
              {swatchOf(option)}
              {option.label}
            </li>
          );
        }}
        size="small"
        // Hidden rather than left inert: an icon that does nothing invites a
        // second click, and the sub-form it opens would be creating a row for
        // a parent the user has not chosen yet.
        startAdornment={
          <>
            {!isInteractionDisabled &&
              filter.subCreationForm &&
              onDisplaySubCreationForm && (
                <CustomAdornment
                  styles={basicsClasses.labelIcon}
                  onOpenSubForm={handleOpenSubForm}
                />
              )}
            {swatchOf(displayedValue)}
          </>
        }
        value={displayedValue}
      />
    </div>
  );
};
